<template>
    <div>
        <p class="title is-7">{{ 'components.common.quick-login-reg.registration.step' | translate }} 1/2</p>

        <form @submit.prevent="submit()" :class="{ 'is-loading' : loading }" role="form">
            <!-- Email input-->
            <div class="field has-addons">
                <div class="control has-icons-left is-expanded">
                    <input v-model.trim="email"
                           v-validate="'required|email|unique-login|min:5|max:50'"
                           data-vv-name="email"
                           data-vv-validate-on="input"
                           :placeholder="'components.common.quick-login-reg.registration.email' | translate"
                           class="input is-small"
                           type="text"
                           autofocus="on"
                           maxlength="100" />
                    <span class="icon is-small is-left">
                        <i class="far fa-envelope"></i>
                    </span>
                    <p class="help is-danger"
                       v-if="errors.has('email:required')">{{"components.common.quick-login-reg.registration.validation.email required" | translate}}</p>
                    <p class="help is-danger"
                       v-if="errors.has('email:min')">{{"components.common.quick-login-reg.registration.validation.email min length" | translate}}</p>
                    <p class="help is-danger"
                       v-if="errors.has('email:max')">{{"components.common.quick-login-reg.registration.validation.email max length" | translate}}</p>
                    <p class="help is-danger"
                       v-if="errors.has('email:unique-login')">{{"components.common.quick-login-reg.registration.validation.email in use" | translate}}</p>
                    <p class="help is-danger"
                       v-if="errors.has('email:email')">{{"components.common.quick-login-reg.registration.validation.email format" | translate}}</p>
                </div>
            </div>
            <!-- Password input-->
            <div class="field">
                <div class="control has-icons-left has-icons-right">
                    <input v-model.trim="password"
                           v-validate="{required:true, min:4, max:20, regex:/^[^\s]+$/}"
                           data-vv-name="password"
                           data-vv-validate-on="input"
                           :placeholder="'components.common.quick-login-reg.registration.password' | translate"
                           class="input is-small"
                           :type="showPassword ? 'text' : 'password'"
                           maxlength="20"
                           ref="password" />
                    <span class="icon is-small is-left">
                        <i class="fas fa-lock"></i>
                    </span>
                    <a class="icon is-small is-right is-clickable"
                       @click="toggleShowPassword('password')">
                        <span v-show="!showPassword">
                            <i class="far fa-eye-slash"></i>
                        </span>
                        <span v-show="showPassword">
                            <i class="far fa-eye"></i>
                        </span>
                    </a>
                    <p class="help is-danger"
                       v-if="errors.has('password:required')">{{"components.common.quick-login-reg.registration.validation.password required" | translate}}</p>
                    <p class="help is-danger"
                       v-if="errors.has('password:min')">{{"components.common.quick-login-reg.registration.validation.password min length" | translate}}</p>
                    <p class="help is-danger"
                       v-if="errors.has('password:max')">{{"components.common.quick-login-reg.registration.validation.password max length" | translate}}</p>
                </div>
            </div>
            <!-- Alias input-->
            <div v-if="askAliasDuringRegistration" class="field">
                <div class="control has-icons-left">
                    <input v-model.trim="alias"
                           v-validate="'min:3|max:50'"
                           data-vv-name="alias"
                           data-vv-validate-on="input"
                           :placeholder="'components.common.quick-login-reg.registration.alias' | translate"
                           class="input is-small"
                           typeof="text"
                           maxlength="50" />
                    <span class="icon is-small is-left">
                        <i class="fas fa-user"></i>
                    </span>
                    <p class="help is-danger"
                       v-if="errors.has('alias:min')">{{"components.common.quick-login-reg.registration.validation.alias min length" | translate}}</p>
                    <p class="help is-danger"
                       v-if="errors.has('alias:max')">{{"components.common.quick-login-reg.registration.validation.alias max length" | translate}}</p>
                </div>
            </div>
            <!-- Currency input-->
            <div v-if="visibleCurrencies && visibleCurrencies.length > 1"
                 class="field center-field">
                <div class="control has-icons-left">
                    <div class="select is-small">
                        <select v-model="currency"
                                v-validate="'required'"
                                data-vv-name="currency"
                                data-vv-validate-on="change">
                            <option :value="null">
                                {{ 'components.common.quick-login-reg.registration.choose currency' | translate }}
                            </option>
                            <option v-for="(c, index) of visibleCurrencies"
                                    :key="index"
                                    :value="c.id">
                                {{ c.description }} ({{ c.id }})
                            </option>
                        </select>
                    </div>
                    <span class="icon is-left">
                        <i class="fas fa-money-bill-alt"></i>
                    </span>
                    <p class="help is-danger"
                       v-if="errors.has('currency:required')">{{"components.common.quick-login-reg.registration.validation.currency required" | translate}}</p>
                </div>
            </div>
            <!-- Agreement input-->
            <div class="field bng-center-field">
                <div class="control">
                    <label class="checkbox">
                        <input v-model="agreement"
                               v-validate="'required'"
                               data-vv-name="agreement"
                               data-vv-validate-on="change"
                               type="checkbox" />
                        <span class="is-size-7">{{'components.pages.registration.agreement' | translate}}</span>
                        <router-link :to="{ name: 'rules-inner', params: { rule: mainRulesRef && mainRulesRef.name } }" class="is-size-7">{{'components.pages.registration.agreement rules' | translate}}</router-link>
                    </label>
                    <p class="help is-danger" v-if="errors.has('agreement:required')">{{"components.pages.registration.validation.agreement required" | translate}}</p>
                </div>
            </div>
            <!-- Recaptcha-->
            <recaptcha v-if="false" :on-response="response => data.recaptchaResponse = response" ref="recaptcha" />

            <button :class="[{ 'is-loading': loading }, 'card-footer-item button is-warning is-small is-fullwidth']">
                <span>{{ "components.common.quick-login-reg.registration.continue" | translate }}</span>
                <span class="icon is-small"><i class="fas fa-arrow-right"></i></span>
            </button>
        </form>
    </div>
</template>

<script>
    import Vue from 'vue';
    import { mapGetters } from 'vuex';

    import registrationService from '../../../../../_common/services/registration.service';
    import ymService from '../../../../../_common/services/ym.service';
    import gaService from '../../../../../_common/services/ga.service';
    import currencyHelper from '../../../../../_common/helpers/currency.helper';
    import rulesHelper from '../../../../../_common/helpers/rules.helper';

    import Recaptcha from '../recaptcha/recaptcha.vue';

    import UniqueLoginValidationRule from '../../../../../_common/mixins/unique-login.validation-rule.mixin';

    export default {
        name: 'quick-login-reg-registration-by-email',
        components: { Recaptcha },
        mixins: [UniqueLoginValidationRule],
        $_veeValidate: { validator: 'new' },
        props: {
            goToStep2: {
                type: Function,
                required: true
            }
        },
        data: () => ({
            email: null,
            password: null,
            alias: null,
            currency: null,
            agreement: false,
            type: 'email',
            recaptchaResponse: null,
            site: window.location.hostname,
            showPassword: false,
            loading: false,
            mainRulesRef: null,
            visibleCurrencies: null
        }),
        computed: {
            ...mapGetters(['client']),
            askAliasDuringRegistration() {
                return this.client && this.client.view.askAliasDuringRegistration;
            },
            showCurrencies() {
                return this.client && this.client.createDefaultBalance;
            },
            registrationBanner() {
                return this.client && this.client.view.registrationBanner;
            },
            lang() {
                return Vue.state.lang.currentLang;
            }
        },
        async created() {
            this.visibleCurrencies = await currencyHelper.getVisibleCurrencies();
            if (this.visibleCurrencies && this.visibleCurrencies.length) {
                this.currency = this.visibleCurrencies[0].id;
            }

            this.mainRulesRef = await rulesHelper.getMainRulesRef(this.lang);
        },
        methods: {
            toggleShowPassword(ref) {
                this.showPassword = !this.showPassword;
                setTimeout(() => this.$refs[ref].focus());
            },
            async submit() {
                await this.$validator.validateAll();
                if (this.errors.any() || this.loading) return;

                this.errors.clear();
                this.loading = true;

                let model = {
                    email: this.email,
                    password: this.password,
                    alias: this.alias,
                    currency: this.currency,
                    type: this.type,
                    recaptchaResponse: this.recaptchaResponse,
                    site: this.site,
                    language: Vue.state.lang.currentLang
                };
                const result = await registrationService.registerByEmail(model);
                if (result.errorData) {
                    result.errorData.forEach(error => {
                        const localize = () => this.$t(`components.common.quick-login-reg.registration.errors.${error.key}.${error.message}`, error.extraParams);
                        this.errors.add({
                            field: error.key,
                            msg: localize(),
                            regenerate: localize
                        });
                    });
                } else {
                    ymService.send('reachGoal', 'REGISTRATION STEP 2');
                    gaService.send('registration', 'registrationstep2', 'registration2');

                    this.goToStep2(result);
                }

                this.loading = false;
            }
        }
    };
</script>

<style lang="scss">
</style>



// WEBPACK FOOTER //
// front/src/skins/bulmasite/components/common/quick-login-reg/registration-by-email.vue